// lib/permissions.jsx — frontend permission helpers.
// Permissions are loaded via /api/auth/me and cached inside AuthContext.
import React, { useMemo } from 'react';
import { useAuth } from './auth';

/**
 * usePermission — returns { has, hasAny, hasAll, all }.
 * `has('users.delete')`, `hasAny('a','b')`, `hasAll('a','b')`
 */
export function usePermission() {
  const { user } = useAuth();
  const list = user?.permissions || [];
  const set = useMemo(() => new Set(list), [list.join('|')]);
  return {
    has:    (p) => set.has(p),
    hasAny: (...ps) => ps.some((p) => set.has(p)),
    hasAll: (...ps) => ps.every((p) => set.has(p)),
    all:    list,
  };
}

/** shortcut hook returning a boolean */
export function useHasPermission(perm) { return usePermission().has(perm); }

/**
 * <Can perm="users.delete">…</Can>
 * <Can anyOf={['a','b']}>…</Can>
 * <Can allOf={['a','b']}>…</Can>
 * Optional `fallback` (render when denied).
 */
export function Can({ perm, anyOf, allOf, children, fallback = null }) {
  const { has, hasAny, hasAll } = usePermission();
  let ok = true;
  if (perm)   ok = has(perm);
  else if (anyOf) ok = hasAny(...anyOf);
  else if (allOf) ok = hasAll(...allOf);
  return ok ? children : fallback;
}
